/* eslint-disable arrow-body-style */
/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/media-has-caption */
import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import Center from '../../assets/styles/Center';
import DefaultContainer from '../../assets/styles/DefaultContainer';
import Error from '../../components/Error';
import FormAuth from '../../components/FormAuth';
import GroupFile from '../../components/GroupFile';
import Loader from '../../components/Loader';
import Title from '../../components/Title';
import {
  useGetPresignedUrl,
  useInvalidateVerification,
  useModelVerification,
} from '../../hooks/queries/useMediaUploads';
import uploadS3 from '../../services/utils/uploadS3';
import { useStore } from '../../stores';
import * as styles from './styles';

export default function ModelUploads() {
  const [loadingForm, setLoadingForm] = useState(false);

  const [files, setFiles] = useState({
    profile_photo: null,
    banner_photo: null,
    verification_media: null,
    document_photo: null,
  });

  const [preview, setPreview] = useState({
    profile_photo: '',
    banner_photo: '',
    document_photo: '',
    verification_media: '',
  });

  const { hash } = useLocation();

  const user = useStore((state) => state.user);

  // ✅ React Query - Verification
  const {
    data: verificationData,
    isLoading,
    isError,
    error: errorVerification,
  } = useModelVerification(user?.id);

  // ✅ Mutations
  const { mutateAsync: getPresignedUrl } = useGetPresignedUrl();
  const invalidateVerification = useInvalidateVerification();

  // ✅ Verificar se o erro é 404 (primeira vez, sem verificação)
  const is404Error = errorVerification?.response?.status === 404;

  // ✅ Update Preview com URLs existentes
  useEffect(() => {
    if (verificationData?.urls) {
      const { urls } = verificationData;

      setPreview({
        profile_photo: urls.profilePhoto || '',
        banner_photo: urls.bannerPhoto || '',
        document_photo: urls.documentPhoto || '',
        verification_media: urls.verificationMedia || '',
      });
    }
  }, [verificationData]);

  const validateFile = useCallback((file, fileType) => {
    const isVideo = fileType === 'verification_media';

    // Set size limit: 50MB movie, 10MB images
    const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;

    // Validation size file
    if (file.size > maxSize) {
      const limit = isVideo ? '50MB' : '10MB';
      toast.error(`Arquivo muito grande. Limite: ${limit}`);
      return false;
    }

    // Video validation type
    if (isVideo && !file.type.startsWith('video/')) {
      toast.error('Por favor, selecione um arquivo de vídeo válido');
      return false;
    }

    // Image validation type
    if (!isVideo && !file.type.startsWith('image/')) {
      toast.error('Por favor, selecione uma imagem válida');
      return false;
    }

    return true;
  }, []);

  // ✅ Required for valid Form
  const isFormValid = useMemo(() => {
    const requiredFiles = [
      'profile_photo',
      'verification_media',
      'document_photo',
    ];

    return requiredFiles.every((type) => files[type] || preview[type]);
  }, [files, preview]);

  const handleFileChange = useCallback(
    (e) => {
      const { id, files: selectedFiles } = e.target;
      const file = selectedFiles[0];

      if (!file) return;

      if (!validateFile(file, id)) {
        e.target.value = '';
        return;
      }

      setFiles((prev) => ({
        ...prev,
        [id]: file,
      }));

      setPreview((prev) => ({
        ...prev,
        [id]: URL.createObjectURL(file),
      }));
    },
    [validateFile]
  );

  // ✅ Processo de upload usando mutations
  const processUploads = useCallback(async () => {
    const uploadsToProcess = [];

    Object.entries(files).forEach(([type, file]) => {
      if (file) {
        const convertLabel = {
          profile_photo: 'profilePhoto',
          banner_photo: 'bannerPhoto',
          verification_media: 'verificationMedia',
          document_photo: 'documentPhoto',
        };

        const label = convertLabel[type];
        uploadsToProcess.push({ label, file, type });
      }
    });

    if (uploadsToProcess.length === 0) {
      toast.error('Nenhum arquivo novo para enviar');
      return false;
    }

    console.log(
      `📤 Iniciando upload de ${uploadsToProcess.length} arquivo(s)...`
    );

    try {
      // ✅ Obter URLs presigned usando mutation
      console.log('🔑 Obtendo URLs presigned...');
      const uploadsWithUrls = await Promise.all(
        uploadsToProcess.map(async ({ file, label, type }) => ({
          file,
          label,
          type,
          url: await getPresignedUrl({ file, label }),
        }))
      );

      console.log(
        `✅ URLs presigned obtidas com sucesso para ${uploadsWithUrls.length} arquivo(s)`
      );

      // ✅ Executar uploads para S3 sequencialmente (um por vez para evitar problemas)
      // eslint-disable-next-line no-await-in-loop
      const results = [];
      const failedUploads = [];

      for (let i = 0; i < uploadsWithUrls.length; i += 1) {
        const { url, file, label, type } = uploadsWithUrls[i];

        try {
          console.log(
            `📤 [${i + 1}/${uploadsWithUrls.length}] Enviando ${label} (${file.name})...`
          );

          // eslint-disable-next-line no-await-in-loop
          await uploadS3(url, file);

          results.push({ label, type, success: true });
          console.log(`✅ [${i + 1}/${uploadsWithUrls.length}] ${label} enviado com sucesso!`);
        } catch (error) {
          console.error(
            `❌ [${i + 1}/${uploadsWithUrls.length}] Falha ao enviar ${label}:`,
            error.message
          );

          failedUploads.push({
            label,
            type,
            file: file.name,
            error: error.message,
          });

          results.push({ label, type, success: false, error: error.message });
        }
      }

      // ✅ Verificar se TODOS os uploads foram bem-sucedidos
      const allSuccess = results.every((r) => r.success);

      if (!allSuccess) {
        console.error('❌ Falhas detectadas:', failedUploads);

        // Montar mensagem detalhada
        const failedNames = failedUploads.map((f) => f.label).join(', ');
        toast.error(
          `Falha ao enviar ${failedUploads.length} arquivo(s): ${failedNames}`,
          { duration: 5000 }
        );

        return false;
      }

      // ✅ TODOS os uploads foram bem-sucedidos
      console.log(
        `✅ Todos os ${results.length} arquivo(s) foram enviados com sucesso!`
      );

      // ✅ Invalidar cache após sucesso TOTAL
      invalidateVerification(user?.id);

      return true;
    } catch (error) {
      console.error('❌ Erro crítico no processo de upload:', error);
      toast.error(`Erro ao processar uploads: ${error.message}`);
      return false;
    }
  }, [files, getPresignedUrl, invalidateVerification, user?.id]);

  const onSubmit = useCallback(async () => {
    if (!isFormValid) {
      toast.error('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    try {
      setLoadingForm(true);

      const success = await processUploads();

      if (success) {
        toast.success('Mídias enviadas com sucesso!');

        // Sinaliza que o modelo foi atualizado
        localStorage.setItem('model_updated', Date.now().toString());

        const nextPage =
          hash === '#edit' ? '/modelvalue#edit' : '/modeldashboard';

        window.location.href = nextPage;
      }
    } catch (error) {
      console.error('Erro no envio:', error);
      toast.error('Erro inesperado. Tente novamente.');
    } finally {
      setLoadingForm(false);
    }
  }, [isFormValid, processUploads, hash]);

  // Clear disassembly
  useEffect(() => {
    return () => {
      Object.values(preview).forEach((url) => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [preview]);

  if (isLoading) {
    return <Loader isLoading />;
  }

  if (isError && !is404Error) {
    return <Error isError message={errorVerification?.message} />;
  }

  return (
    <DefaultContainer>
      <Center className="center">
        <Title
          title="Sempre Atualizado"
          subtitle="Deixe seu perfil"
          two
          small
        />

        <FormAuth
          buttonTitle="Enviar mídias"
          next
          formValid={isFormValid}
          isLoading={loadingForm}
          onSubmit={onSubmit}
        >
          <styles.Row>
            <GroupFile
              title="Foto de perfil"
              type={1}
              idForm="profile_photo"
              prev={{ profile_photo: preview.profile_photo }}
            >
              <input
                id="profile_photo"
                type="file"
                name="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileChange}
              />
            </GroupFile>

            <GroupFile
              title="Banner"
              type={2}
              idForm="banner_photo"
              prev={{ banner_photo: preview.banner_photo }}
              required={false}
            >
              <input
                id="banner_photo"
                type="file"
                name="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileChange}
              />
            </GroupFile>
          </styles.Row>

          <styles.Row>
            <GroupFile
              title="Documento"
              type={3}
              idForm="document_photo"
              prev={{ document_photo: preview.document_photo }}
            >
              <input
                id="document_photo"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileChange}
              />
            </GroupFile>

            <GroupFile
              title="Mídia de Comparação"
              type={4}
              idForm="verification_media"
              video
              prev={{ verification_media: preview.verification_media }}
            >
              <input
                id="verification_media"
                type="file"
                name="file"
                accept="video/mp4,video/webm,video/ogg"
                onChange={handleFileChange}
              />
            </GroupFile>
          </styles.Row>
        </FormAuth>
      </Center>
    </DefaultContainer>
  );
}
